import { useState } from 'react';
import { FiX, FiSave } from 'react-icons/fi';
import { apiCreateBlogPost, apiUpdateBlogPost } from '../../api';
import { emitAdminToast } from '../../utils/adminToast';

// Auto-generate a URL-safe slug from a title.
const toSlug = (s) =>
  String(s)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');

// `content` is stored as a JSON array of paragraph strings. The form exposes
// a single textarea (one paragraph per blank-line block) and converts on save.
const blocksToText = (blocks) => {
  if (!Array.isArray(blocks)) return '';
  return blocks.map((b) => (typeof b === 'string' ? b : (b?.text || ''))).join('\n\n');
};

const textToBlocks = (text) =>
  String(text || '')
    .split(/\n{2,}/)
    .map((b) => b.trim())
    .filter(Boolean);

const BlogPostForm = ({ post, onClose, onSaved }) => {
  const isEdit = !!post;
  const [form, setForm] = useState({
    slug: post?.slug || '',
    title: post?.title || '',
    author: post?.author || '',
    authorRole: post?.authorRole || '',
    authorAvatar: post?.authorAvatar || '',
    excerpt: post?.excerpt || '',
    date: post?.date || '',
    readTime: post?.readTime || '',
    image: post?.image || '',
    tag: post?.tag || '',
    tagsCsv: Array.isArray(post?.tags) ? post.tags.join(', ') : '',
    contentText: blocksToText(post?.content),
    isPublished: post?.isPublished ?? true,
    position: post?.position ?? 0,
  });
  const [saving, setSaving] = useState(false);

  const update = (k) => (e) => {
    const val = e?.target?.type === 'checkbox' ? e.target.checked : e?.target?.value;
    setForm((prev) => ({ ...prev, [k]: val }));
  };

  // Auto-derive slug from title when creating a new post and slug is empty.
  const handleTitleChange = (e) => {
    const title = e.target.value;
    setForm((prev) => ({
      ...prev,
      title,
      slug: !isEdit && !prev.slug ? toSlug(title) : prev.slug,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.slug.trim() || !form.author.trim()) {
      emitAdminToast('Title, slug and author are required', 'error');
      return;
    }
    const tags = form.tagsCsv
      .split(',').map((t) => t.trim()).filter(Boolean);

    const payload = {
      slug: form.slug.trim(),
      title: form.title.trim(),
      author: form.author.trim(),
      authorRole: form.authorRole || null,
      authorAvatar: form.authorAvatar || null,
      excerpt: form.excerpt || null,
      date: form.date || null,
      readTime: form.readTime ? Number.parseInt(form.readTime, 10) : null,
      image: form.image || null,
      tag: form.tag || null,
      tags,
      content: textToBlocks(form.contentText),
      isPublished: !!form.isPublished,
      position: Number(form.position) || 0,
    };

    if (payload.content.length === 0) {
      emitAdminToast('Content cannot be empty', 'error');
      return;
    }

    setSaving(true);
    try {
      if (isEdit) await apiUpdateBlogPost(post.id, payload);
      else await apiCreateBlogPost(payload);
      onSaved();
    } catch { /* API client displays the error toast. */ } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex justify-end">
      <div className="bg-white w-full max-w-2xl h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b px-5 py-3 flex items-center justify-between z-10">
          <h2 className="font-semibold text-black">
            {isEdit ? 'Edit Post' : 'New Blog Post'}
          </h2>
          <button onClick={onClose} className="text-black hover:text-black">
            <FiX size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4 text-sm">
          <div>
            <label className="block text-xs font-medium text-black mb-1">Title *</label>
            <input
              type="text" value={form.title} onChange={handleTitleChange}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              required
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-black mb-1">Slug *</label>
            <input
              type="text" value={form.slug} onChange={update('slug')}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 font-mono text-xs"
              placeholder="my-post-slug" required
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-black mb-1">Author *</label>
              <input
                type="text" value={form.author} onChange={update('author')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2" required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-black mb-1">Author Role</label>
              <input
                type="text" value={form.authorRole} onChange={update('authorRole')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-black mb-1">Author Avatar URL</label>
            <input
              type="text" value={form.authorAvatar} onChange={update('authorAvatar')}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-black mb-1">Excerpt</label>
            <textarea
              rows={2} value={form.excerpt} onChange={update('excerpt')}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-black mb-1">Date (display)</label>
              <input
                type="text" value={form.date} onChange={update('date')}
                placeholder="June 15, 2026"
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-black mb-1">Read Time (min)</label>
              <input
                type="number" min="1" value={form.readTime} onChange={update('readTime')}
                placeholder="5"
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-black mb-1">Position</label>
              <input
                type="number" min="0" value={form.position} onChange={update('position')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-black mb-1">Cover Image URL</label>
            <input
              type="text" value={form.image} onChange={update('image')}
              placeholder="https://... or /uploads/foo.jpg"
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-black mb-1">Primary Tag</label>
              <input
                type="text" value={form.tag} onChange={update('tag')}
                placeholder="Fiction"
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-black mb-1">Tags (CSV)</label>
              <input
                type="text" value={form.tagsCsv} onChange={update('tagsCsv')}
                placeholder="books, reading, fiction"
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-black mb-1">
              Content * <span className="text-black font-normal">(separate paragraphs with a blank line)</span>
            </label>
            <textarea
              rows={12} value={form.contentText} onChange={update('contentText')}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 font-mono text-xs"
              required
            />
          </div>

          <label className="inline-flex items-center gap-2">
            <input
              type="checkbox" checked={!!form.isPublished} onChange={update('isPublished')}
            />
            <span className="text-sm text-black">Published</span>
          </label>

          <div className="flex items-center justify-end gap-2 pt-2 border-t">
            <button
              type="button" onClick={onClose}
              className="px-4 py-2 text-sm text-black hover:text-black"
            >
              Cancel
            </button>
            <button
              type="submit" disabled={saving}
              className="flex items-center gap-2 bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange-700 disabled:opacity-50"
            >
              <FiSave size={16} />
              {saving ? 'Saving...' : isEdit ? 'Update Post' : 'Create Post'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BlogPostForm;
